import React from 'react';
import { Topic } from '../../../types/topic';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import TopicsGroup from '../TopicsGroup';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// Define letter ranges for different languages
const LETTER_RANGES = {
  he: /[\u0590-\u05FF]/,  // Hebrew letters
  yi: /[\u0590-\u05FF]/,  // Yiddish uses Hebrew letters
  en: /[A-Z]/,            // English letters
  es: /[A-ZÁÉÍÓÚÑ]/       // Spanish letters with accents
};

interface TopicsListProps {
  topics: Topic[];
  searchQuery: string;
}

const TopicsList: React.FC<TopicsListProps> = ({ topics, searchQuery }) => {
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const { t, i18n } = useTranslation();

  const groupedTopics = React.useMemo(() => {
    const filtered = topics.filter(topic => 
      topic.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return filtered.reduce((acc: { [key: string]: Topic[] }, topic) => {
      const firstLetter = topic.title[0].toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(topic);
      return acc;
    }, {});
  }, [topics, searchQuery]);

  const sortedLetters = React.useMemo(() => {
    const letters = Object.keys(groupedTopics);
    const currentLangRegex = LETTER_RANGES[i18n.language as keyof typeof LETTER_RANGES] || LETTER_RANGES.en;
    
    // Sort letters into two groups: current language and others
    const currentLangLetters = letters.filter(letter => currentLangRegex.test(letter));
    const otherLetters = letters.filter(letter => !currentLangRegex.test(letter));
    
    // Sort each group alphabetically
    currentLangLetters.sort((a, b) => a.localeCompare(b, i18n.language));
    otherLetters.sort((a, b) => a.localeCompare(b, 'en')); // Use English sorting for non-language letters
    
    // Combine the groups
    return [...currentLangLetters, ...otherLetters];
  }, [groupedTopics, i18n.language]);

  if (sortedLetters.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`
          flex flex-col items-center justify-center py-24
          ${theme.mode === 'dark' 
            ? 'bg-surface-secondary/5' 
            : 'bg-surface-primary/5'}
          rounded-2xl
        `}
      >
        <div className={`
          w-20 h-20 mb-8 p-4 rounded-2xl
          ${theme.mode === 'dark' 
            ? 'bg-surface-secondary/20 text-text-secondary/40' 
            : 'bg-surface-primary/30 text-text-secondary/50'}
        `}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-2xl font-medium mb-3">{t('search.noResults', { query: searchQuery })}</h3>
        <p className={`text-base ${theme.mode === 'dark' ? 'text-text-secondary opacity-70' : 'text-text-secondary opacity-80'}`}>
          {t('search.tryDifferentSearch')}
        </p>
      </motion.div>
    );
  }

  return (
    <div className="relative">
      {sortedLetters.map((letter, index) => (
        <TopicsGroup
          key={letter}
          letter={letter}
          topics={groupedTopics[letter].sort((a, b) => 
            isRTL ? b.title.localeCompare(a.title) : a.title.localeCompare(b.title)
          )}
        />
      ))}
    </div>
  );
};

export default TopicsList; 